import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { GuestBookingProvider } from '@contexts/GuestBookingContext'
import { BookingFlowProvider } from '@contexts/BookingFlowContext'
import { ChatProvider } from '@contexts/ChatContext'
import { SWRProvider } from '@providers/SWRProvider'
import { ThemeProvider } from '@contexts/ThemeContext'
import { validateEnvironment } from '@utils/validateEnv'
import DevBanner from '@components/DevBanner'
import { TwentyFirstToolbar } from '@21st-extension/toolbar-react'
import { ReactPlugin } from '@21st-extension/react'
import { supabase } from '@lib/supabase'
import AdminRoutes from "@/AdminRoutes";
import RoseApp, { isRoseExperience } from '@/rose/RoseApp'

//-----------------------
// Layout Components
import PublicLayout from '@components/PublicLayout'
import ClientLayout from '@components/ClientLayout'
import TalentLayout from '@components/TalentLayout'
//import AdminLayout from '@components/AdminLayout'
import ScrollToTop from '@components/ScrollToTop'
import ChatBubble from '@components/chat/ChatBubble'

// Page Loading Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
  </div>
)

// Public Pages (loaded immediately)
import Home from '@pages/Home'
import Login from '@pages/Login'
import SignUp from '@pages/SignUp'
import SignupDemo from '@pages/SignupDemo'
import SignupEnhanced from '@pages/SignupEnhanced'
import ForgotPassword from '@pages/ForgotPassword'
import ResetPassword from '@pages/ResetPassword'
import AuthCallback from '@pages/AuthCallback'
import CompleteGoogleSignup from '@pages/CompleteGoogleSignup'
import NotFound from '@pages/NotFound'
import Forbidden from '@pages/Forbidden'
import TestConnection from '@pages/TestConnection'
import PrivacyPolicy from '@pages/PrivacyPolicy'
import TermsAndConditions from '@pages/TermsAndConditions'
import Contact from '@pages/Contact'
import TestSupabase from '@pages/TestSupabase'
import About from '@pages/About'
import HowItWorks from '@pages/HowItWorks'
import ClientPricing from '@pages/ClientPricing'
import TalentPricing from '@pages/pricing/TalentPricing'
import FAQ from '@pages/FAQ'
import Guidelines from '@pages/Guidelines'
import ProfilePage from '@pages/customer/ProfilePage'
import Referrals from '@pages/customer/Referrals'
import Demo from '@pages/Demo'
import TalentApplication from '@pages/TalentApplication'
import Gallery from '@pages/Gallery'
import PhotographyFundamentals from '@pages/training/PhotographyFundamentals'
import TrainingPurchase from '@pages/training/TrainingPurchase'
import TrainingSuccess from '@pages/training/TrainingSuccess'
import TrainingRedeem from '@pages/training/TrainingRedeem'
import RedditLanding from '@pages/RedditLanding'

//AUTH CONTEXT FOR USERS REGULAR
// ⚠️ Lazy import AuthContext so it doesn’t run for /admin/*
const AuthProviderLazy = lazy(() => import('@contexts/AuthContext').then(m => ({ default: m.AuthProvider })));
const ProtectedRouteLazy = lazy(() => import('@contexts/AuthContext').then(m => ({ default: m.ProtectedRoute })));

// Customer Pages (lazy loaded)
const CustomerDashboard = lazy(() => import('@pages/customer/Dashboard'))
const BrowsePhotographers = lazy(() => import('@pages/photographers/PhotographersPage'))
const Guide = lazy(() => import('@pages/customer/Guide'))
const BookingConfirmation = lazy(() => import('@pages/customer/BookingConfirmation'))
const Quiz = lazy(() => import('@pages/customer/Quiz'))
const BookingDetails = lazy(() => import('@pages/customer/BookingDetails'))

// Lazy load placeholder pages
const PhotographerProfile = lazy(() => import('@pages/customer/PhotographerProfile'))
const BookingPage = lazy(() =>
  import('@pages/customer/placeholders').then(module => ({ default: module.BookingPage }))
)

// Booking wizard pages
const PackageSelection = lazy(() => import('@pages/customer/booking/PackageSelection'))
const VideoSelection = lazy(() => import('@pages/customer/booking/VideoSelection'))
const AddOnsDetails = lazy(() => import('@pages/customer/booking/AddOnsDetails'))
const ManageAddOns = lazy(() => import('@pages/customer/booking/ManageAddOns'))
const AccountSetup = lazy(() => import('@pages/customer/booking/AccountSetup'))
const ContractStep = lazy(() => import('@pages/customer/booking/ContractStep'))
const PaymentStep = lazy(() => import('@pages/customer/booking/PaymentStep'))
const PaymentSuccess = lazy(() => import('@pages/customer/booking/PaymentSuccess'))
const ChangeDateSuccess = lazy(() => import('@pages/customer/booking/ChangeDateSuccess'))
const GiftRedemption    = lazy(() => import('@pages/customer/GiftRedemption'))
const CheckoutComplete = lazy(() => import('./pages/customer/booking/CheckoutComplete'))
const CheckoutResume = lazy(() => import('@pages/customer/CheckoutResume'))
const BookingFlowGuard = lazy(() => import('@components/booking/BookingFlowGuard'))
const ScheduleRedirect = lazy(() => import('@components/booking/ScheduleRedirect'))
const SelectVideographer = lazy(() => import('@pages/customer/booking/SelectVideographer'))
const SelectSecondPhotographer = lazy(() => import('@pages/customer/booking/SelectSecondPhotographer'))
const SelectEngagementPhotographer = lazy(() => import('@pages/customer/booking/SelectEngagementPhotographer'))
const ExpressCheckout = lazy(() => import('@pages/customer/booking/ExpressCheckout'))
const BookingSuccess = lazy(() => import('@pages/customer/booking/BookingSuccess'))
const CustomerBookings = lazy(() =>
  import('@pages/customer/placeholders').then(module => ({ default: module.CustomerBookings }))
)
const ReviewSubmission = lazy(() => import('@pages/customer/ReviewSubmission'))

// Photographer Pages (lazy loaded)
const PhotographerJobQueue = lazy(() => import('@pages/photographer/JobQueue'))
const PhotographerUploads = lazy(() => import('@pages/photographer/Uploads'))

// Talent Dashboard Pages (lazy loaded)
const TalentDashboardLayout = lazy(() => import('@components/talent/TalentDashboardLayout'))
const TalentOverview = lazy(() => import('@pages/talent/dashboard/OverviewPage'))
const TalentProfile = lazy(() => import('@pages/talent/dashboard/ProfilePage'))
const TalentCalendar = lazy(() => import('@pages/talent/dashboard/CalendarPage'))
const TalentResources = lazy(() => import('@pages/talent/dashboard/ResourcesPage'))
const TalentAffiliateLinks = lazy(() => import('@pages/talent/dashboard/AffiliateLinksPage'))
const TalentMessages = lazy(() => import('@pages/talent/dashboard/MessagesPage'))
const TalentBookings = lazy(() => import('@pages/talent/dashboard/BookingsPage'))
const TalentSettings = lazy(() => import('@pages/talent/dashboard/SettingsPage'))
const TalentAvailability = lazy(() => import('@pages/talent/dashboard/AvailabilityPage'))
const VideographerProfile = lazy(() => import('@pages/talent/dashboard/videographer/VideographerProfilePage'))
const TalentTraining = lazy(() => import('@pages/talent/TalentTraining'))
const TalentQuizPage = lazy(() => import('@pages/talent/dashboard/TalentQuizPage'))

// Lazy load photographer placeholders
const PhotographerDashboard = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerDashboard }))
)
const PhotographerOnboarding = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerOnboarding }))
)
const PhotographerAvailability = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerAvailability }))
)
const PhotographerPortfolio = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerPortfolio }))
)
const PhotographerPackages = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerPackages }))
)
const PhotographerJobs = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerJobs }))
)
const PhotographerTraining = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerTraining }))
)
const PhotographerSettings = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerProfile }))
)
const PhotographerEarnings = lazy(() =>
  import('@pages/placeholders').then(module => ({ default: module.PhotographerEarnings }))
)

// Admin Pages (lazy loaded)

function App() {
  // Hooks first, returns after (2 Oct 2026): the Rose and console returns used to come before these effects, so a
  // render that crossed between the site and /lp-console changed the hook count and React could throw. The effects
  // are the public site's only, so they skip themselves on the Rose host and the console.
  const rose = isRoseExperience()
  const isAdminRoute = location.pathname.startsWith("/lp-console");
  const siteOnly = !rose && !isAdminRoute

  useEffect(() => {
    if (!siteOnly) return
    console.log('[Supabase Client]', supabase)
  }, [siteOnly])
  // Check environment configuration on mount
  useEffect(() => {
    if (!siteOnly) return
    const env = validateEnvironment()

    if (!env.isValid) {
      console.warn('🚨 Environment Configuration Issues Detected:')

      if (env.missing.length > 0) {
        console.warn('❌ Missing variables:', env.missing.join(', '))
      }

      if (env.placeholder.length > 0) {
        console.warn('⚠️ Using placeholder values:', env.placeholder.join(', '))
      }

      console.log('\n📚 Quick Setup Guide:')
      console.log('1. Create a Supabase project at https://app.supabase.com')
      console.log('2. Get your Stripe keys from https://dashboard.stripe.com')
      console.log('3. Update the .env file with your credentials')
      console.log('4. Restart the development server\n')
    } else {
      console.log('✅ Environment properly configured')
    }

    // Log configuration status for debugging
    if (import.meta.env.DEV) {
      console.log('📊 Configuration Status:', {
        supabase: env.isSupabaseConfigured() ? '✅' : '❌',
        stripe: env.isStripeConfigured() ? '✅' : '❌',
        appUrl: env.all.VITE_APP_URL || 'Not set'
      })
    }
  }, [siteOnly])

  // Safety guard to ensure title is always 'Love & Photos'
  useEffect(() => {
    if (!siteOnly) return
    if (document?.title !== 'Love & Photos') {
      document.title = 'Love & Photos'
    }
  }, [siteOnly])

  if (rose) {
    return <RoseApp />
  }

  if (isAdminRoute) {
  return (
    <SWRProvider>
      <ThemeProvider>
        {/* ✅ No <Routes> wrapper — AdminRoutes already defines them */}
        <AdminRoutes />
      </ThemeProvider>
    </SWRProvider>
  );
  }

  return (
    <SWRProvider>
      <ThemeProvider>



        <AuthProviderLazy>
          <ChatProvider>
            <GuestBookingProvider>
              <BookingFlowProvider>
                <div className="min-h-screen bg-background text-foreground antialiased">
          {/* 21st.dev Toolbar - Development only */}
          <TwentyFirstToolbar
            config={{
              plugins: [ReactPlugin]
            }}
          />

        {/* Scroll to top on navigation */}
        <ScrollToTop />

        {/* Development environment banner */}
        {import.meta.env.DEV && <DevBanner />}

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              borderRadius: '8px',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />

        <Routes>
          {/* Public Routes - No lazy loading for better UX */}
          {/* <Route path="/admin/*" element={<AdminRoutes />} /> */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signin" element={<Navigate to="/login" replace />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/get-started" element={<Navigate to="/signup" replace />} />
            <Route path="/signup-demo" element={<SignupDemo />} />
            <Route path="/signup-enhanced" element={<SignupEnhanced />} />
            <Route path="/talent/apply" element={<TalentApplication />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/complete-signup" element={<CompleteGoogleSignup />} />
            <Route path="/test" element={<TestConnection />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<Navigate to="/terms" replace />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/reddit" element={<RedditLanding />} />
            <Route path="/test-supabase" element={<TestSupabase />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing/client" element={<ClientPricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/resources" element={<Navigate to="/guidelines" replace />} />
            <Route path="/learn" element={<Navigate to="/guidelines" replace />} />
            <Route path="/guidelines" element={<Guidelines />} />
            <Route path="/training/photography-fundamentals" element={<PhotographyFundamentals />} />
            <Route path="/training/photography-fundamentals/purchase" element={<TrainingPurchase />} />
            <Route path="/training/photography-fundamentals/success" element={<TrainingSuccess />} />
            <Route path="/training/redeem" element={<TrainingRedeem />} />
            <Route path="/photographer-resources" element={<Navigate to="/guidelines" replace />} />
            <Route path="/photographer-faq" element={<FAQ />} />
            <Route path="/join" element={<SignUp />} />
            {/* Redirect /talent to application gateway */}
            <Route path="/talent" element={<Navigate to="/talent/apply" replace />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/gallery" element={<Gallery />} />
            {/* Sales-only portfolio directory. Intentionally not linked in public nav. */}
            <Route path="/photographers" element={
              <Suspense fallback={<PageLoader />}>
                <BrowsePhotographers />
              </Suspense>
            } />
            <Route path="/videographers" element={<Navigate to="/" replace />} />
            <Route path="/guide" element={
              <Suspense fallback={<PageLoader />}>
                <Guide />
              </Suspense>
            } />
            <Route path="/how-to-book" element={
              <Suspense fallback={<PageLoader />}>
                <Guide />
              </Suspense>
            } />
          </Route>

          {/* Customer Routes - Lazy loaded */}
          <Route element={<ClientLayout />}>
            <Route path="/dashboard" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <CustomerDashboard />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/browse" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <BrowsePhotographers />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/photographer/:id" element={
              <Suspense fallback={<PageLoader />}>
                <PhotographerProfile />
              </Suspense>
            } />
            <Route path="/book/:photographerId" element={
              <Suspense fallback={<PageLoader />}>
                <BookingPage />
              </Suspense>
            } />

            {/* Express Checkout - Standalone booking flow */}
            <Route path="/book" element={
              <Suspense fallback={<PageLoader />}>
                <ExpressCheckout />
              </Suspense>
            } />

            {/* Express Checkout Success */}
            <Route path="/book/success" element={
              <Suspense fallback={<PageLoader />}>
                <BookingSuccess />
              </Suspense>
            } />

            {/* Booking Wizard Routes */}
            {/* New Package Selection Flow */}
            <Route path="/booking/:photographerId/packages" element={
              <Suspense fallback={<PageLoader />}>
                <PackageSelection />
              </Suspense>
            } />
            <Route path="/booking/:photographerId/video" element={
              <Suspense fallback={<PageLoader />}>
                <VideoSelection />
              </Suspense>
            } />

            {/* Legacy Schedule Route - Keep for backwards compatibility */}
            <Route path="/booking/:photographerId/schedule" element={
              <Suspense fallback={<PageLoader />}>
                <ScheduleRedirect />
              </Suspense>
            } />
            {/* Videographer Selection Route - With photographerId parameter */}
            <Route path="/booking/:photographerId/select-videographer" element={
              <Suspense fallback={<PageLoader />}>
                <SelectVideographer />
              </Suspense>
            } />
            {/* Second Photographer Selection Route - With photographerId parameter */}
            <Route path="/booking/:photographerId/select-second-photographer" element={
              <Suspense fallback={<PageLoader />}>
                <SelectSecondPhotographer />
              </Suspense>
            } />
            {/* Engagement Session Photographer Selection Route - With photographerId parameter */}
            <Route path="/booking/:photographerId/select-engagement-photographer" element={
              <Suspense fallback={<PageLoader />}>
                <SelectEngagementPhotographer />
              </Suspense>
            } />
            <Route path="/booking/:photographerId/addons" element={
              <Suspense fallback={<PageLoader />}>
                <BookingFlowGuard requiredStep="addons">
                  <AddOnsDetails />
                </BookingFlowGuard>
              </Suspense>
            } />
            <Route path="/booking/:photographerId/account" element={
              <Suspense fallback={<PageLoader />}>
                <BookingFlowGuard requiredStep="account">
                  <AccountSetup />
                </BookingFlowGuard>
              </Suspense>
            } />
            <Route path="/booking/:photographerId/contract" element={
              <Suspense fallback={<PageLoader />}>
                <BookingFlowGuard requiredStep="contract">
                  <ContractStep />
                </BookingFlowGuard>
              </Suspense>
            } />
            <Route path="/booking/:photographerId/payment" element={
              <Suspense fallback={<PageLoader />}>
                <BookingFlowGuard requiredStep="payment">
                  <PaymentStep />
                </BookingFlowGuard>
              </Suspense>
            } />
            {/* Payment Success Route */}
            <Route path="/booking/:bookingId/payment/success" element={
              <Suspense fallback={<PageLoader />}>
                <PaymentSuccess />
              </Suspense>
            } />
            {/* Gift Session Redemption — public, no auth required */}
            <Route path="/gift/redeem/:code" element={
              <Suspense fallback={<PageLoader />}>
                <GiftRedemption />
              </Suspense>
            } />
            {/* Date Change Success Route */}
            <Route path="/booking/:bookingId/change-date/success" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <ChangeDateSuccess />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            {/* Manage Add-Ons Route */}
            <Route path="/manage-addons/:bookingId" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <ManageAddOns />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/booking/:photographerId/confirm" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <BookingConfirmation />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/book/quiz/:bookingId" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <Quiz />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/my-bookings" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <CustomerBookings />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/booking/:bookingId" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <BookingDetails />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/review/:bookingId" element={
              <ProtectedRouteLazy>
                <Suspense fallback={<PageLoader />}>
                  <ReviewSubmission />
                </Suspense>
              </ProtectedRouteLazy>
            } />
            <Route path="/profile" element={
              <ProtectedRouteLazy>
                <ProfilePage />
              </ProtectedRouteLazy>
            } />
            <Route path="/dashboard/referrals" element={
              <ProtectedRouteLazy>
                <Referrals />
              </ProtectedRouteLazy>
            } />
          </Route>

          {/* Checkout Resume Route (for abandoned cart emails) */}
          <Route path="/checkout/:bookingId" element={
            <Suspense fallback={<PageLoader />}>
              <CheckoutResume />
            </Suspense>
          } />

          {/* Checkout Complete Route (for Stripe redirect) */}
          <Route path="/checkout/complete" element={
            <Suspense fallback={<PageLoader />}>
              <CheckoutComplete />
            </Suspense>
          } />

          {/* Legacy Photographer Routes - Redirect to new Talent Dashboard */}
          <Route path="/dashboard/photographer" element={<Navigate to="/talent/dashboard" replace />} />
          <Route path="/dashboard/photographer/job-queue" element={<Navigate to="/talent/dashboard" replace />} />
          <Route path="/dashboard/photographer/uploads" element={<Navigate to="/talent/dashboard" replace />} />

          {/* Photographer Routes - Lazy loaded (Keep for backward compatibility) */}
          <Route path="/pricing/talent" element={
            <ProtectedRouteLazy requireRole="photographer">
              <TalentLayout>
                <TalentPricing />
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/dashboard/photographer-legacy" element={
            <ProtectedRouteLazy requireRole="photographer">
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerDashboard />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/dashboard/photographer/job-queue" element={
            <ProtectedRouteLazy requireRole="photographer" requireOnboarding>
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerJobQueue />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/dashboard/photographer/uploads" element={
            <ProtectedRouteLazy requireRole="photographer" requireOnboarding>
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerUploads />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/onboarding/photographer" element={
            <ProtectedRouteLazy requireRole="photographer">
              <Suspense fallback={<PageLoader />}>
                <PhotographerOnboarding />
              </Suspense>
            </ProtectedRouteLazy>
          } />
          <Route path="/photographer/availability" element={
            <ProtectedRouteLazy requireRole="photographer" requireOnboarding>
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerAvailability />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/photographer/portfolio" element={
            <ProtectedRouteLazy requireRole="photographer" requireOnboarding>
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerPortfolio />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/photographer/packages" element={
            <ProtectedRouteLazy requireRole="photographer" requireOnboarding>
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerPackages />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/photographer/jobs" element={
            <ProtectedRouteLazy requireRole="photographer" requireOnboarding>
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerJobs />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/photographer/training" element={
            <ProtectedRouteLazy requireRole="photographer">
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerTraining />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/photographer/profile" element={
            <ProtectedRouteLazy requireRole="photographer">
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerSettings />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />
          <Route path="/photographer/earnings" element={
            <ProtectedRouteLazy requireRole="photographer" requireOnboarding>
              <TalentLayout>
                <Suspense fallback={<PageLoader />}>
                  <PhotographerEarnings />
                </Suspense>
              </TalentLayout>
            </ProtectedRouteLazy>
          } />

          {/* Talent Training Route - Required before dashboard access */}
          <Route path="/talent/training" element={
            <ProtectedRouteLazy requireRole="photographer">
              <Suspense fallback={<PageLoader />}>
                <TalentTraining />
              </Suspense>
            </ProtectedRouteLazy>
          } />

          {/* Talent Quiz Route - Required before dashboard access */}
          <Route path="/talent/dashboard/quiz/:quizId" element={
            <ProtectedRouteLazy requireRole="photographer">
              <Suspense fallback={<PageLoader />}>
                <TalentQuizPage />
              </Suspense>
            </ProtectedRouteLazy>
          } />

          {/* Talent Dashboard Routes - New Dashboard System */}
          <Route path="/talent/dashboard" element={
            <ProtectedRouteLazy requireRole="photographer">
              <Suspense fallback={<PageLoader />}>
                <TalentDashboardLayout />
              </Suspense>
            </ProtectedRouteLazy>
          }>
            <Route index element={
              <Suspense fallback={<PageLoader />}>
                <TalentOverview />
              </Suspense>
            } />
            <Route path="profile" element={
              <Suspense fallback={<PageLoader />}>
                <TalentProfile />
              </Suspense>
            } />
            <Route path="calendar" element={
              <Suspense fallback={<PageLoader />}>
                <TalentCalendar />
              </Suspense>
            } />
            <Route path="resources" element={
              <Suspense fallback={<PageLoader />}>
                <TalentResources />
              </Suspense>
            } />
            <Route path="resources/affiliate-links" element={
              <Suspense fallback={<PageLoader />}>
                <TalentAffiliateLinks />
              </Suspense>
            } />
            <Route path="messages" element={
              <Suspense fallback={<PageLoader />}>
                <TalentMessages />
              </Suspense>
            } />
            <Route path="bookings" element={
              <Suspense fallback={<PageLoader />}>
                <TalentBookings />
              </Suspense>
            } />
            <Route path="settings" element={
              <Suspense fallback={<PageLoader />}>
                <TalentSettings />
              </Suspense>
            } />
            <Route path="availability" element={
              <Suspense fallback={<PageLoader />}>
                <TalentAvailability />
              </Suspense>
            } />
          </Route>

          {/* Videographer Dashboard Route - Nested Routes Structure */}
          <Route path="/talent/dashboard/videographer" element={
            <ProtectedRouteLazy requireRole="photographer">
              <Suspense fallback={<PageLoader />}>
                <TalentDashboardLayout />
              </Suspense>
            </ProtectedRouteLazy>
          }>
            <Route index element={
              <Suspense fallback={<PageLoader />}>
                <TalentOverview />
              </Suspense>
            } />
            <Route path="profile" element={
              <Suspense fallback={<PageLoader />}>
                <VideographerProfile />
              </Suspense>
            } />
            <Route path="calendar" element={
              <Suspense fallback={<PageLoader />}>
                <TalentCalendar />
              </Suspense>
            } />
            <Route path="resources" element={
              <Suspense fallback={<PageLoader />}>
                <TalentResources />
              </Suspense>
            } />
            <Route path="resources/affiliate-links" element={
              <Suspense fallback={<PageLoader />}>
                <TalentAffiliateLinks />
              </Suspense>
            } />
            <Route path="messages" element={
              <Suspense fallback={<PageLoader />}>
                <TalentMessages />
              </Suspense>
            } />
            <Route path="bookings" element={
              <Suspense fallback={<PageLoader />}>
                <TalentBookings />
              </Suspense>
            } />
            <Route path="settings" element={
              <Suspense fallback={<PageLoader />}>
                <TalentSettings />
              </Suspense>
            } />
            <Route path="availability" element={
              <Suspense fallback={<PageLoader />}>
                <TalentAvailability />
              </Suspense>
            } />
          </Route>
          {/* None of these are used */}
          {/* Admin Routes - Lazy loaded */}
          {/* <Route path="/admin" element={
            <ProtectedRoute requireRole="admin">
              <AdminLayout>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/photographers" element={
            <ProtectedRoute requireRole="admin">
              <AdminLayout>
                <Suspense fallback={<PageLoader />}>
                  <AdminPhotographerManagement />
                </Suspense>
              </AdminLayout>
            </ProtectedRoute>
          } /> */}
          {/* None of these are used */}
          {/* Redirect old /admin URL to new console path */}
          <Route path="/admin" element={<Navigate to="/lp-console" replace />} />
          <Route path="/admin/*" element={<Navigate to="/lp-console" replace />} />

          {/* 403 Forbidden Page */}
          <Route path="/forbidden" element={<Forbidden />} />
          
          {/* 404 Page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
              </div>

              {/* Chat Bubble - shows for customers and photographers with active chats */}
              <ChatBubble />
            </BookingFlowProvider>
          </GuestBookingProvider>
          </ChatProvider>
        </AuthProviderLazy>
      </ThemeProvider>
    </SWRProvider>
  )
}

export default App
